import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminHeaders, validateAdminSession } from '../../utils/adminAuth';
import './FixMidnightHoursPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const FixMidnightHoursPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fixedCount, setFixedCount] = useState<number | null>(null);

  const loadCount = useCallback(async () => {
    const response = await fetch(`${API_BASE_URL}/admin/checkins/fix-midnight-hours`, {
      headers: getAdminHeaders(),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || 'Failed to count sessions');
    }
    setCount(typeof data.count === 'number' ? data.count : 0);
  }, []);

  useEffect(() => {
    const check = async () => {
      const session = await validateAdminSession();
      if (!session) {
        navigate('/admin-login');
        return;
      }
      if (session.mustResetPassword) {
        navigate('/admin/change-password');
        return;
      }
      if (session.role !== 'breakglass') {
        navigate('/admin-panel');
        return;
      }
      try {
        await loadCount();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to count sessions');
      } finally {
        setLoading(false);
      }
    };
    check();
  }, [navigate, loadCount]);

  const fixSessions = async () => {
    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/admin/checkins/fix-midnight-hours`, {
        method: 'POST',
        headers: getAdminHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fix sessions');
      }
      setFixedCount(typeof data.sessionsFixed === 'number' ? data.sessionsFixed : 0);
      setConfirming(false);
      await loadCount();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fix sessions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fix-hours-container">
        <div className="fix-hours-card">Loading...</div>
      </div>
    );
  }

  return (
    <div className="fix-hours-container">
      <div className="fix-hours-card">
        <h1>Fix forgotten sign-outs</h1>
        <p className="fix-hours-copy">
          Older versions checked people out at midnight when they forgot to sign out,
          so those days show many more hours than they worked. This finds sessions that
          ended at midnight Pacific and lasted more than 4 hours, then sets each one to
          exactly 3 hours from its check-in time. A shift of 4 hours or less, such as
          8 p.m. to midnight, is left alone. No emails are sent.
        </p>
        <p className="fix-hours-count">
          {count === null ? '' : `${count} session${count === 1 ? '' : 's'} match.`}
        </p>
        {fixedCount !== null && (
          <p className="fix-hours-done">Updated {fixedCount} session{fixedCount === 1 ? '' : 's'} to 3 hours.</p>
        )}
        {error && <p className="fix-hours-error">{error}</p>}
        <button
          type="button"
          className="fix-hours-start"
          disabled={!count || saving}
          onClick={() => setConfirming(true)}
        >
          Set matching sessions to 3 hours
        </button>
        <button type="button" className="fix-hours-back" onClick={() => navigate('/admin-panel')}>
          Back to admin panel
        </button>
      </div>

      {confirming && (
        <div className="fix-hours-overlay" role="alertdialog" aria-modal="true">
          <div className="fix-hours-warning">
            <h2>Set {count} midnight session{count === 1 ? '' : 's'} to exactly 3 hours?</h2>
            <p>Each matching checkout time becomes 3 hours after its check-in. This can be run again later; sessions already at 3 hours are skipped.</p>
            <div className="fix-hours-warning-actions">
              <button type="button" onClick={() => setConfirming(false)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="fix-hours-confirm" onClick={fixSessions} disabled={saving}>
                {saving ? 'Updating...' : 'Set to 3 hours'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FixMidnightHoursPage;
